import { Suspense, useEffect, useState } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { Image } from '@/routes/-components/image';
import { NgoProjectCard } from './ngo-project-card';
import type { PartnerNgo } from './use-partner-ngos';

/** The hand-off's grid is 2x2, so a page is four projects — which is what the arrows step through. */
const PROJECTS_PER_PAGE = 4;

/** A 32px round control from the modal's top bar. White on the scrim, per the hand-off. */
function BarButton(props: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
	return (
		<button
			type="button"
			onClick={props.onClick}
			disabled={props.disabled}
			aria-label={props.label}
			className="text-ink grid size-8 shrink-0 cursor-pointer place-items-center rounded-full bg-white transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-40"
		>
			{props.children}
		</button>
	);
}

/**
 * The NGO's projects, one page at a time.
 *
 * Its own component so the Suspense boundary covers the grid alone — the sheet, its header and the
 * NGO lockup are already known when the modal opens and must not wait on a fetch. It owns the page
 * count and reports it upward, because only it knows how many projects came back.
 */
function ProjectsGrid(props: { ngo: PartnerNgo; page: number; onPageCount: (count: number) => void }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { data } = useSuspenseQuery(
		trpc.portal.getPartnerNgoProjects.queryOptions({ ngoId: props.ngo.id, locale: language }),
	);

	const pageCount = Math.max(1, Math.ceil(data.projects.length / PROJECTS_PER_PAGE));
	const onPageCount = props.onPageCount;
	useEffect(() => onPageCount(pageCount), [pageCount, onPageCount]);

	if (data.projects.length === 0) {
		// lingui disallows a property access inside t`` — bind it to a plain variable first.
		const name = props.ngo.name;
		const noProjectsCopy = t`${name} has no open projects at the moment. Take a look at the other organisations in the directory.`;
		return (
			<div className="flex flex-col items-center gap-4 py-16 text-center">
				<img
					src="/redesign/partner-ngos/illo-folder.svg"
					alt=""
					aria-hidden
					className="h-auto w-[200px]"
					width={200}
					height={159}
				/>
				<div className="flex flex-col gap-1">
					<p className="text-xl leading-[1.3] font-medium">{t`No projects to show right now.`}</p>
					<p className="text-ink/60 text-sm leading-[1.5]">{noProjectsCopy}</p>
				</div>
			</div>
		);
	}

	const start = props.page * PROJECTS_PER_PAGE;
	return (
		<div className="grid gap-6 xl:grid-cols-2">
			{data.projects.slice(start, start + PROJECTS_PER_PAGE).map((project) => (
				<NgoProjectCard key={project.id} project={project} />
			))}
		</div>
	);
}

/**
 * "Mira los proyectos más recientes" — the sheet the directory opens when a partner's logo is clicked.
 *
 * The top-bar arrows page through THIS partner's projects, four at a time. They used to step between
 * NGOs, which read as navigating away from the thing the visitor had just clicked; the sheet is about
 * one organisation, and closing it is how you get back to the others.
 *
 * Built from the Radix primitives rather than the shared `DialogContent`, which is a centred, sm-width
 * app dialog: this one is a full-bleed bottom sheet with its own scrim (ink at 25% behind a 3px blur)
 * and a control bar that sits OUTSIDE the white panel. Overriding all of that through `className`
 * would be a longer and more fragile list than composing the four primitives directly.
 */
export function NgoProjectsModal(props: { ngo: PartnerNgo | null; onOpenChange: (ngo: PartnerNgo | null) => void }) {
	const { t } = useLingui();
	const [page, setPage] = useState(0);
	const [pageCount, setPageCount] = useState(1);
	const ngo = props.ngo;
	const ngoId = ngo?.id;

	// Opening a different partner starts at its first page rather than wherever the last one was left.
	useEffect(() => {
		setPage(0);
		setPageCount(1);
	}, [ngoId]);

	return (
		<DialogPrimitive.Root open={!!ngo} onOpenChange={(open) => !open && props.onOpenChange(null)}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 fixed inset-0 z-50 bg-[#25302c]/25 backdrop-blur-[3px]" />
				<DialogPrimitive.Content
					className="data-open:animate-in data-closed:animate-out data-closed:slide-out-to-bottom data-open:slide-in-from-bottom fixed inset-x-0 top-8 bottom-0 z-50 flex flex-col gap-8 duration-200 outline-none"
					aria-describedby={undefined}
				>
					{ngo && (
						/* Keyed on the partner so everything below is a fresh subtree whenever the sheet is
						   reopened for a different one. Without it the lockup kept the previous partner's
						   artwork: the `<picture>` element was reused, and a browser that has already picked a
						   candidate out of `srcSet` does not redo that choice when the attribute changes
						   underneath it. `contents` keeps the wrapper out of the flex layout. */
						<div key={ngo.id} className="contents">
							<div className="mx-auto flex w-full max-w-[1376px] shrink-0 items-center px-4">
								{/* Only once there is a second page to reach. A partner with four projects or fewer
								   showed two permanently-disabled arrows, which reads as something broken rather
								   than as an absence. `pageCount` starts at 1 and is corrected by the grid when the
								   query lands, so they appear with the projects rather than flickering before them. */}
								{pageCount > 1 && (
									<div className="flex items-center gap-2">
										<BarButton
											label={t`Previous projects`}
											onClick={() => setPage((current) => current - 1)}
											disabled={page <= 0}
										>
											<ArrowLeft className="size-4" />
										</BarButton>
										<BarButton
											label={t`More projects`}
											onClick={() => setPage((current) => current + 1)}
											disabled={page >= pageCount - 1}
										>
											<ArrowRight className="size-4" />
										</BarButton>
									</div>
								)}
								{/* `ml-auto` on a wrapper rather than `justify-between`: with the arrows gone the close
								   button is the only child and `justify-between` would park it on the left. It goes on
								   the wrapper because `asChild` hands its props to `BarButton`, which does not spread
								   them onto the element. */}
								<div className="ml-auto">
									<DialogPrimitive.Close asChild>
										<BarButton label={t`Close`} onClick={() => props.onOpenChange(null)}>
											<X className="size-4" />
										</BarButton>
									</DialogPrimitive.Close>
								</div>
							</div>

							{/* The panel runs off the bottom of the viewport by design — only its top corners are
							    rounded, and it is the scroll container for the whole grid. */}
							<div className="flex-1 overflow-y-auto rounded-t-3xl bg-white">
								<div className="mx-auto max-w-[1440px] p-6 lg:p-8">
									<div className="flex items-center gap-5">
										{ngo.logo && (
											<div className="grid size-20 shrink-0 place-items-center rounded-lg bg-white p-3 shadow-[0_10.8px_28.8px_0_rgba(46,20,0,0.12)]">
												<Image
													src={ngo.logo.location}
													alt={ngo.name}
													height={56}
													aspectRatio={1.6}
													objectFit="contain"
													className="max-h-14 w-full object-contain!"
												/>
											</div>
										)}
										<DialogPrimitive.Title className="text-2xl leading-[1.3] font-medium md:text-[2rem]">
											{t`See the most recent projects`}
										</DialogPrimitive.Title>
									</div>

									<div className="mt-8">
										<Suspense fallback={<div className="min-h-[313px]" />}>
											<ProjectsGrid ngo={ngo} page={page} onPageCount={setPageCount} />
										</Suspense>
									</div>
								</div>
							</div>
						</div>
					)}
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
